import { action, makeObservable, observable } from 'mobx';
import Dexie from 'dexie';
import 'dexie-observable';
import type { RootStore } from 'logic';

const LOCAL_DB_VERSION = 1;
const SESSION_SOURCE_FIELDS_KEY = 'sessionSourceFields';
interface SessionSourceFieldsEntry {
  sourceId: string;
  sessionId: string;
  fields: string[];
  showPowerColumn?: boolean;
  showAttachmentColumn?: boolean;
  showDownloadsColumn?: boolean;
}

export class UiSourceFieldsStore {
  /** Configure dropdown dialog - persisted in store so it survives source updates (add/delete options) */
  configureDialogSourceId: string | null = null;
  configureDialogFieldId: string | null = null;

  fieldsById: Record<
    string,
    Record<
      string,
      {
        showPowerColumn?: boolean;
        showAttachmentColumn?: boolean;
        showDownloadsColumn?: boolean;
        fields: string[];
      }
    >
  > = {};
  localDb: Dexie;
  sessionSourceFields: Dexie.Table<SessionSourceFieldsEntry, [string, string]>;
  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable<UiSourceFieldsStore, 'updateLocalFieldCache'>(this, {
      fieldsById: observable,
      configureDialogSourceId: observable,
      configureDialogFieldId: observable,
      openConfigureDialog: action,
      closeConfigureDialog: action,
      updateLocalFieldCache: action,
      setFields: action.bound,
    });

    const localDb = new Dexie('UiSourceFieldsStore');
    localDb.version(LOCAL_DB_VERSION).stores({
      [SESSION_SOURCE_FIELDS_KEY]: '&[sessionId+sourceId]',
    });
    this.localDb = localDb;
    this.sessionSourceFields = localDb.table(SESSION_SOURCE_FIELDS_KEY);
    this.rootStore = rootStore;
  }

  openConfigureDialog = (sourceId: string, fieldId: string) => {
    this.configureDialogSourceId = sourceId;
    this.configureDialogFieldId = fieldId;
  };

  closeConfigureDialog = () => {
    this.configureDialogSourceId = null;
    this.configureDialogFieldId = null;
  };

  private updateLocalFieldCache(entry: SessionSourceFieldsEntry) {
    if (!entry || typeof entry !== 'object' || entry.sessionId == null || entry.sourceId == null) {
      return;
    }
    if (!this.fieldsById[entry.sessionId]) {
      this.fieldsById[entry.sessionId] = {};
    }
    this.fieldsById[entry.sessionId][entry.sourceId] = {
      fields: entry.fields ?? [],
      showPowerColumn: entry.showPowerColumn,
      showAttachmentColumn: entry.showAttachmentColumn,
      showDownloadsColumn: entry.showDownloadsColumn,
    };
  }

  async init() {
    this.localDb.on('changes', (changes) => {
      changes.forEach((change) => {
        const obj = 'obj' in change ? (change as { obj?: unknown }).obj : undefined;
        if (obj && typeof obj === 'object' && 'sessionId' in obj && 'sourceId' in obj) {
          this.updateLocalFieldCache(obj as SessionSourceFieldsEntry);
        }
      });
    });

    // Load initial data; guard against Dexie throwing on corrupt/malformed IndexedDB
    try {
      const data = await this.sessionSourceFields.toArray();
      (data ?? []).forEach((entry) => {
        if (entry && entry.sessionId != null && entry.sourceId != null) {
          this.updateLocalFieldCache(entry);
        }
      });
    } catch (e) {
      // Dexie can throw e.g. TypeError if IndexedDB has corrupt/malformed records
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[UiSourceFieldsStore] Failed to load local field cache:', e);
      }
    }
  }

  togglePowerColumn = (sourceId: string) => {
    const currentSessionId = this.rootStore.sessionsStore.currentSession?.id;
    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (currentSessionId && source) {
      const showPowerColumn = this.fieldsById[currentSessionId]?.[sourceId]?.showPowerColumn;
      const currentSourceFields = this.fieldsById[currentSessionId]?.[sourceId]?.fields;
      const fields = (currentSourceFields || source.data.fieldIds).slice(0);
      this.sessionSourceFields.put({
        sourceId,
        sessionId: currentSessionId,
        fields,
        showPowerColumn: showPowerColumn === undefined ? false : !showPowerColumn,
      });
    }
  };

  toggleAttachmentColumn = (sourceId: string) => {
    const currentSessionId = this.rootStore.sessionsStore.currentSession?.id;
    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (currentSessionId && source) {
      const showAttachmentColumn =
        this.fieldsById[currentSessionId]?.[sourceId]?.showAttachmentColumn;
      const currentSourceFields = this.fieldsById[currentSessionId]?.[sourceId]?.fields;
      const fields = (currentSourceFields || source.data.fieldIds).slice(0);
      this.sessionSourceFields.put({
        sourceId,
        sessionId: currentSessionId,
        fields,
        showAttachmentColumn: showAttachmentColumn === undefined ? true : !showAttachmentColumn,
      });
    }
  };

  toggleDownloadsColumn = (sourceId: string) => {
    const currentSessionId = this.rootStore.sessionsStore.currentSession?.id;
    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (currentSessionId && source) {
      const showDownloadsColumn =
        this.fieldsById[currentSessionId]?.[sourceId]?.showDownloadsColumn;
      const currentSourceFields = this.fieldsById[currentSessionId]?.[sourceId]?.fields;
      const fields = (currentSourceFields || source.data.fieldIds).slice(0);
      this.sessionSourceFields.put({
        sourceId,
        sessionId: currentSessionId,
        fields,
        showDownloadsColumn: !showDownloadsColumn,
      });
    }
  };

  /**
   * Ensure a newly created field is visible by adding it to the stored fields list.
   * Only needed when a custom fields list already exists (user has toggled columns before).
   * If no custom list exists, all fields are visible by default via source.data.fieldIds fallback.
   */
  ensureFieldVisible = (sourceId: string, fieldId: string) => {
    const currentSessionId = this.rootStore.sessionsStore.currentSession?.id;
    if (!currentSessionId) return;
    const currentSourceFields = this.fieldsById[currentSessionId]?.[sourceId]?.fields;
    if (currentSourceFields && !currentSourceFields.includes(fieldId)) {
      this.sessionSourceFields.put({
        sourceId,
        sessionId: currentSessionId,
        fields: [...currentSourceFields, fieldId],
        showPowerColumn: this.fieldsById[currentSessionId]?.[sourceId]?.showPowerColumn,
        showAttachmentColumn: this.fieldsById[currentSessionId]?.[sourceId]?.showAttachmentColumn,
        showDownloadsColumn: this.fieldsById[currentSessionId]?.[sourceId]?.showDownloadsColumn,
      });
    }
  };

  toggleField = (sourceId: string, field: string) => {
    const currentSessionId = this.rootStore.sessionsStore.currentSession?.id;
    const source = this.rootStore.sourcesStore.sources[sourceId];
    if (currentSessionId && source) {
      const currentSourceFields = this.fieldsById[currentSessionId]?.[sourceId]?.fields;
      const fields = currentSourceFields || source.data.fieldIds;
      const newFields = fields.includes(field)
        ? fields.filter((f) => f !== field)
        : [...fields, field];
      this.sessionSourceFields.put({
        sourceId,
        sessionId: currentSessionId,
        fields: newFields,
      });
    }
  };

  setFields = (sourceId: string, fields: string[]) => {
    const currentSessionId = this.rootStore.sessionsStore.currentSession?.id;
    const existingEntry = currentSessionId
      ? this.fieldsById[currentSessionId]?.[sourceId]
      : undefined;

    if (!currentSessionId) return;

    this.sessionSourceFields.put({
      sourceId,
      sessionId: currentSessionId,
      fields,
      showPowerColumn: existingEntry?.showPowerColumn,
      showAttachmentColumn: existingEntry?.showAttachmentColumn,
      showDownloadsColumn: existingEntry?.showDownloadsColumn,
    });
  };
}
